import React, { useState, useRef, useEffect } from 'react';
import ClariMeLogo from './ClariMeLogo';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { suggestEmail } from '../utils/emailSuggest';

// ── Icons ───────────────────────────────────────────────────────────

const ArrowLeftIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m0 0l7 7m-7-7l7-7" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const ContactBookIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const EditIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ── Phone formatting ────────────────────────────────────────────────

function formatPhoneInput(value) {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function formatPhoneDisplay(digits) {
  if (!digits || digits.length !== 10) return digits || '';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ── Component ───────────────────────────────────────────────────────

export default function CreateCareGroupView({ onSave, onCancel }) {
  const keyboardHeight = useKeyboardHeight();
  const [groupName, setGroupName] = useState('');
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [relationship, setRelationship] = useState('');
  const [hasContactPicker, setHasContactPicker] = useState(false);
  const nameInputRef = useRef(null);
  const groupNameRef = useRef(null);

  useEffect(() => {
    setHasContactPicker('contacts' in navigator && 'ContactsManager' in window);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => groupNameRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showForm) {
      const timer = setTimeout(() => nameInputRef.current?.focus(), 200);
      return () => clearTimeout(timer);
    }
  }, [showForm]);

  // Native contact picker
  const handlePickContacts = async () => {
    try {
      const selected = await navigator.contacts.select(
        ['name', 'tel', 'email'],
        { multiple: true }
      );
      const newMembers = selected
        .filter(c => c.name?.[0])
        .map(c => {
          const rawPhone = (c.tel?.[0] || '').replace(/\D/g, '');
          const digits = rawPhone.length === 11 && rawPhone.startsWith('1')
            ? rawPhone.slice(1)
            : rawPhone;
          const contactEmail = (c.email?.[0] || '').trim().toLowerCase();
          return {
            id: Date.now() + Math.random(),
            name: c.name[0],
            email: contactEmail,
            phone: digits,
            relationship: '',
          };
        })
        .filter(newC => !members.some(existing =>
          (existing.email && newC.email && existing.email === newC.email) ||
          (existing.phone && newC.phone && existing.phone === newC.phone)
        ));
      if (newMembers.length > 0) {
        setMembers(prev => [...prev, ...newMembers]);
      }
    } catch (err) {
      // User cancelled or API error — that's fine
    }
  };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  // Manual add
  const handleAddMember = () => {
    if (!name.trim() || !isValidEmail(email)) return;
    const digits = phone.replace(/\D/g, '');
    setMembers(prev => [...prev, {
      id: Date.now(),
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: digits,
      relationship: relationship.trim(),
    }]);
    setName('');
    setEmail('');
    setPhone('');
    setRelationship('');
    setShowForm(false);
  };

  const handleRemove = (id) => {
    setMembers(prev => prev.filter(m => m.id !== id));
  };

  const handleSave = () => {
    const finalName = groupName.trim() || 'My Care Group';
    onSave({
      name: finalName,
      members,
    });
  };

  return (
    <div className="fixed inset-x-0 top-0 flex flex-col bg-white" style={{ height: keyboardHeight > 0 ? `calc(100% - ${keyboardHeight}px)` : '100dvh' }}>
      {/* Header */}
      <div className="flex-shrink-0 pt-safe pb-3 px-6 flex items-center gap-3 border-b border-slate-100">
        <button
          onClick={onCancel}
          className="w-9 h-9 rounded-xl flex items-center justify-center active:bg-slate-100 transition-colors -ml-1"
        >
          <ArrowLeftIcon className="w-5 h-5 text-slate-400" />
        </button>
        <h1 className="text-lg font-semibold text-slate-900">Create a Care Group</h1>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 max-w-sm mx-auto w-full">
        {/* Group name */}
        <div className="mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-teal-100/60">
            <UsersIcon className="w-7 h-7 text-teal-600" />
          </div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Group name</label>
          <input
            ref={groupNameRef}
            type="text"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            placeholder="e.g. Mom's Cancer Care, Dad's Cardiology"
            className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all text-slate-900 placeholder:text-slate-300"
          />
          <p className="text-xs text-slate-400 mt-1.5">Choose a name your family will recognize.</p>
        </div>

        {/* Members section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Family members
            {members.length > 0 && <span className="text-slate-400 font-normal"> · {members.length} added</span>}
          </label>

          {/* Added members */}
          {members.length > 0 && (
            <div className="space-y-2 mb-3">
              {members.map(m => (
                <div key={m.id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-violet-600">
                      {m.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 text-sm truncate">{m.name}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {m.email || m.relationship || (m.phone ? formatPhoneDisplay(m.phone) : 'No contact info')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(m.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-300 active:text-red-400 active:bg-red-50 transition-colors"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add buttons */}
          {!showForm && (
            <div className="space-y-2">
              {hasContactPicker && (
                <button
                  onClick={handlePickContacts}
                  className="w-full flex items-center justify-center gap-2.5 py-3 rounded-xl bg-white border-2 border-slate-200 text-sm font-semibold text-slate-700 active:border-teal-300 active:text-teal-700 active:bg-teal-50/30 active:scale-[0.98] transition-all shadow-sm"
                >
                  <ContactBookIcon className="w-5 h-5" />
                  Choose from contacts
                </button>
              )}
              <button
                onClick={() => setShowForm(true)}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                  hasContactPicker
                    ? 'border-2 border-dashed border-slate-200 text-slate-500 active:border-teal-300 active:text-teal-600 active:bg-teal-50/30'
                    : 'bg-white border-2 border-slate-200 text-slate-700 font-semibold active:border-teal-300 active:text-teal-700 active:bg-teal-50/30 shadow-sm'
                }`}
              >
                <EditIcon className="w-4 h-4" />
                Enter details manually
              </button>
            </div>
          )}

          {/* Manual entry form */}
          {showForm && (
            <div className="bg-white rounded-2xl border-2 border-teal-200 p-4 shadow-sm">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g. Sarah"
                    autoComplete="off"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all text-slate-900 placeholder:text-slate-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="sarah@example.com"
                    autoComplete="off"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all text-slate-900 placeholder:text-slate-300"
                  />
                  {suggestEmail(email) && (
                    <button
                      type="button"
                      onClick={() => setEmail(suggestEmail(email))}
                      className="mt-1.5 text-sm text-amber-600 font-medium active:text-amber-700"
                    >
                      Did you mean <span className="underline">{suggestEmail(email)}</span>?
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Phone number <span className="text-slate-300">(optional)</span></label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={e => setPhone(formatPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 10)))}
                    placeholder="(555) 867-5309"
                    autoComplete="off"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all text-slate-900 placeholder:text-slate-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Relationship <span className="text-slate-300">(optional)</span></label>
                  <input
                    type="text"
                    value={relationship}
                    onChange={e => setRelationship(e.target.value)}
                    placeholder="e.g. Daughter, Spouse, Sibling"
                    autoComplete="off"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all text-slate-900 placeholder:text-slate-300"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => { setShowForm(false); setName(''); setEmail(''); setPhone(''); setRelationship(''); }}
                    className="flex-1 py-3 rounded-xl text-sm font-medium text-slate-500 bg-slate-100 active:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddMember}
                    disabled={!name.trim() || !isValidEmail(email)}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 shadow-md shadow-teal-500/20 active:shadow-lg active:scale-[0.98] transition-all disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={members.length === 0}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all disabled:opacity-40 disabled:active:scale-100"
        >
          Create Care Group
        </button>
        <p className="text-xs text-slate-400 text-center mt-3 mb-8">
          Members will receive summaries via email when you share a visit.
        </p>
      </div>
    </div>
  );
}
